import { useState } from 'react';
import { Link } from 'react-router';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Button } from '@xiltrion/ui/button';
import { Skeleton } from '@xiltrion/ui/skeleton';
import { Spinner } from '@xiltrion/ui/spinner';
import type { CaseDetail, Task } from '@/api/types';
import { useSession } from '@/app/session';
import { DueBadge, ErrorState, StatusTag, fmtDate } from '@/components/ui';
import { useTask } from '@/features/tasks/api';
import { AssigneeHint, TaskWork } from '@/features/tasks/TaskWorkPanel';
import { ACTION_LABEL, FINISHED_STATUSES, finishedAt } from '../lib';

/**
 * Columna derecha del expediente: la próxima acción completa (la tarea tal como se trabaja en `/tareas/:id`, en modo
 * compacto) o, si no hay tarea abierta, el estado del proceso. El tono dice de quién es: primario si es tuya, neutro si
 * es de otra persona, destructivo si está bloqueada.
 */
export function NextActionCard({ c }: { c: CaseDetail }) {
  const next = c.nextAction;
  const closed = ['CLOSED', 'ARCHIVED'].includes(c.status);
  if (!next || closed) return <CaseStateCard c={c} />;
  return <TaskCard key={next.id} c={c} next={next} />;
}

/** El tono va solo en el encabezado; el borde de la tarjeta es el normal. */
const TONE = {
  mine: { head: 'bg-primary/10', icon: 'text-primary', mark: 'mdi:play-circle' as NombreIcono },
  other: { head: 'bg-muted', icon: 'text-muted-foreground', mark: 'mdi:clock-outline' as NombreIcono },
  blocked: { head: 'bg-destructive/10', icon: 'text-destructive', mark: 'mdi:lock-outline' as NombreIcono },
};

function TaskCard({ c, next }: { c: CaseDetail; next: Task }) {
  const { me } = useSession();
  const q = useTask(next.id);
  /** Donde el panel de la tarea pone su menú «Acciones de la tarea». */
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const mine = next.assigneeUserId === me?.userId;
  const tone = TONE[next.status === 'BLOCKED' ? 'blocked' : mine ? 'mine' : 'other'];
  const others = c.openTasks.length - 1;
  const steps = q.data?.steps ?? [];
  const step = steps.length > 1 ? steps.findIndex((s) => s.state === 'current') + 1 : 0;
  return (
    <section aria-label="Próxima acción" className="bg-card border rounded-lg">
      {/* Sticky inside the scrolling column: the title stays while the body scrolls. `bg-card` under the translucent tone keeps it opaque. */}
      <header className="sticky top-0 z-10 bg-card rounded-t-md">
      <div className={`px-6 py-4 flex flex-wrap items-start gap-x-3 gap-y-1 rounded-t-md ${tone.head}`}>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground tnum flex items-center gap-1.5 mb-1"><Icono nombre={tone.mark} className={`text-base ${tone.icon}`} aria-hidden /> Próxima acción{step > 0 ? ` · paso ${step} de ${steps.length}` : ''}</div>
          <h2 className="text-lg font-semibold m-0 leading-tight">{ACTION_LABEL[next.actionCode] ?? next.name}</h2>
          <div className="text-sm text-muted-foreground mt-0.5">
            {mine ? 'Asignada a ti' : next.assigneeUserId ? <>Asignada a {next.assigneeName ?? 'otra persona'}{q.data && <> <AssigneeHint task={q.data} me={me?.userId} /></>}</> : 'Sin responsable · puedes tomarla'}
            {next.status === 'BLOCKED' && <span className="text-destructive"> · bloqueada</span>}
            {others > 0 && <> · <Link to={`/pqrs/${c.id}?tab=workflow`}>{others} tarea{others === 1 ? '' : 's'} más abierta{others === 1 ? '' : 's'}</Link></>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 ml-auto">
          <DueBadge due={q.data?.due ?? next.due} dueAt={q.data?.dueAt ?? next.dueAt} />
          <div className="flex flex-col items-end">
            <div ref={setActionsSlot} className="inline-flex" />
            <Button asChild variant="ghost" size="xs" className="text-muted-foreground"><Link to={`/tareas/${next.id}`}><Icono nombre="mdi:arrow-expand" aria-hidden /> Pantalla completa</Link></Button>
          </div>
        </div>
      </div>
      </header>
      <div className="p-6">
        {q.isLoading ? <div className="flex flex-col gap-3" role="status" aria-label="Cargando tarea"><Skeleton className="h-16" /><Skeleton className="h-10" /><Skeleton className="h-40" /></div>
          : q.isError || !q.data ? <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar la tarea." />
          : <TaskWork task={q.data} compact actionsSlot={actionsSlot} />}
      </div>
    </section>
  );
}

/** Sin tarea abierta: cerrado, en marcha (el motor clasifica, asigna o avanza entre pasos) o sin nada pendiente. */
function CaseStateCard({ c }: { c: CaseDetail }) {
  const finished = finishedAt(c);
  const working = !FINISHED_STATUSES.includes(c.status) && c.status !== 'TRANSFERRED';
  const body = FINISHED_STATUSES.includes(c.status)
    ? { icon: 'mdi:lock-check-outline' as NombreIcono, title: c.status === 'RESOLVED' ? 'Expediente resuelto' : 'Expediente cerrado', text: finished ? `El ${fmtDate(finished, false)}. No tiene acciones pendientes.` : 'No tiene acciones pendientes.' }
    : c.status === 'WAITING_INFORMATION'
      ? { icon: 'mdi:account-clock-outline' as NombreIcono, title: 'Esperando al ciudadano', text: 'El caso espera la información que se le pidió al ciudadano. Cuando responda, el flujo continúa y la próxima acción aparecerá aquí.' }
    : working
      ? { icon: null, title: 'El proceso está en marcha', text: ['RECEIVED', 'PROCESSING', 'PENDING_ASSIGNMENT'].includes(c.status) ? 'El sistema está clasificando y asignando el caso. La próxima acción aparecerá aquí.' : 'El flujo avanza al siguiente paso. La próxima acción aparecerá aquí.' }
      : { icon: 'mdi:check-circle-outline' as NombreIcono, title: 'Sin acciones pendientes', text: 'Este expediente no tiene tareas abiertas.' };
  return (
    <section aria-label="Próxima acción" className="bg-card border rounded-lg p-4 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">{body.icon ? <Icono nombre={body.icon} className="text-base" aria-hidden /> : <Spinner className="size-4" />} Próxima acción</div>
        <h2 className="text-lg font-semibold m-0 leading-tight flex items-center gap-2 flex-wrap">{body.title} <StatusTag status={c.status} /></h2>
        <p className="m-0 mt-1 text-sm text-muted-foreground">{body.text}</p>
      </div>
    </section>
  );
}
